import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Sparkles, ArrowLeft, Check, Crown, Loader2 } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import SiteFooter from '../components/SiteFooter';
import { toast } from 'sonner';

const Premium = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState('');
  const highlightedSpread = new URLSearchParams(location.search).get('spread');

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/me/subscription`);
      setSubscription(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (spreadType) => {
    setCheckoutLoading(spreadType);
    try {
      const response = await axios.post(`${API}/checkout/session`, {
        origin_url: window.location.origin,
        spread_type: spreadType,
      });
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not initiate checkout');
      setCheckoutLoading('');
    }
  };

  const features = [
    { free: true, text: 'Daily reading' },
    { free: true, text: 'Single Card spread' },
    { free: true, text: 'Three Card spread (Past/Present/Future)' },
    { free: true, text: 'Reading history' },
    { free: true, text: 'Card encyclopedia' },
  ];

  const paidProducts = [
    {
      id: 'five-card',
      title: 'Five Card Cross',
      kicker: 'Decision reading',
      cards: 5,
      price: subscription?.prices?.['five-card'] || 4.99,
      creditKey: 'five_card_credits',
      button: 'Buy 5-Card',
      description: 'One paid 5-card reading for a specific problem, choice, or path.',
    },
    {
      id: 'celtic-cross',
      title: 'Celtic Cross',
      kicker: 'Deep reading',
      cards: 10,
      price: subscription?.prices?.['celtic-cross'] || 9.99,
      creditKey: 'celtic_cross_credits',
      button: 'Buy 10-Card',
      description: 'One paid 10-card reading for complex, long-range situations.',
    },
  ];

  const fiveCardCredits = subscription?.five_card_credits || 0;
  const celticCredits = subscription?.celtic_cross_credits || 0;
  const hasCredits = fiveCardCredits > 0 || celticCredits > 0;

  return (
    <div className="min-h-screen bg-[#050505] text-[#F8F9FA]">
      <nav className="glass-effect border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
            className="flex items-center gap-2 text-zinc-400 hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            <span>Dashboard</span>
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="text-2xl font-heading font-light">Seatask Arcane AI</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12" data-testid="premium-page">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6">
            <Crown className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Per-Game Reading Credits</span>
          </div>
          <h1 className="text-5xl sm:text-6xl font-heading font-light tracking-tighter mb-4">
            Buy a Reading Credit
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Pay once, play once. No monthly subscription.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin" />
          </div>
        ) : (
          <>
          {hasCredits && (
            <div className="glass-effect rounded-lg p-8 mb-6 text-center" data-testid="active-subscription">
              <Crown className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="text-3xl font-heading font-medium mb-3">Reading Credit Ready</h2>
              <p className="text-zinc-400">
                5-card credits: {fiveCardCredits} · 10-card credits: {celticCredits}
              </p>
              <button
                onClick={() => navigate('/reading/new')}
                data-testid="use-reading-credit-button"
                className="mt-6 px-8 py-3 bg-[#D4AF37] text-[#050505] rounded-md hover:bg-[#F3E5AB] transition-all font-medium"
              >
                Use Reading Credit
              </button>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Free Tier */}
            <div className="glass-effect rounded-lg p-8">
              <h3 className="text-2xl font-heading font-medium mb-2">Seeker</h3>
              <p className="text-zinc-500 text-sm mb-6">Forever free</p>
              <div className="mb-8">
                <span className="text-4xl font-heading font-light">$0</span>
                <span className="text-zinc-500 ml-2">Forever Free</span>
              </div>
              <ul className="space-y-3 mb-8">
                {features.filter((f) => f.free).map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm">
                    <Check className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                    <span className="text-zinc-300">{f.text}</span>
                  </li>
                ))}
              </ul>
              <button
                disabled
                className="w-full py-3 border border-white/10 text-zinc-500 rounded-md cursor-not-allowed"
              >
                Current Plan
              </button>
            </div>

            {paidProducts.map((product) => {
              const isHighlighted = highlightedSpread === product.id;
              const loadingThis = checkoutLoading === product.id;
              return (
                <div
                  key={product.id}
                  className={`rounded-lg p-8 border-2 relative bg-gradient-to-b from-[#0F0F11] to-[#1a1408] ${
                    isHighlighted ? 'border-[#F3E5AB]' : 'border-[#D4AF37]'
                  }`}
                  data-testid={`paid-tier-${product.id}`}
                >
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#D4AF37] text-[#050505] text-xs uppercase tracking-wider rounded-full font-medium">
                    {product.kicker}
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                    <h3 className="text-2xl font-heading font-medium">{product.title}</h3>
                  </div>
                  <p className="text-zinc-400 text-sm mb-6">{product.description}</p>
                  <div className="mb-8">
                    <span className="text-4xl font-heading font-light text-[#D4AF37]">${product.price}</span>
                    <span className="text-zinc-500 ml-2">/ per game</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-center gap-3 text-sm">
                      <Check className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                      <span className="text-zinc-100">One {product.cards}-card reading credit</span>
                    </li>
                    <li className="flex items-center gap-3 text-sm">
                      <Check className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                      <span className="text-zinc-100">One-time Stripe card payment</span>
                    </li>
                    <li className="flex items-center gap-3 text-sm">
                      <Check className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                      <span className="text-zinc-100">No monthly billing</span>
                    </li>
                  </ul>
                  <button
                    onClick={() => handleUpgrade(product.id)}
                    disabled={!!checkoutLoading}
                    data-testid={product.id === 'five-card' ? 'buy-five-card-button' : 'upgrade-button'}
                    className="w-full py-3 bg-[#D4AF37] text-[#050505] rounded-md hover:bg-[#F3E5AB] transition-all font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loadingThis ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Redirecting...
                      </>
                    ) : (
                      <>
                        <Crown className="w-4 h-4" strokeWidth={1.5} />
                        {product.button}
                      </>
                    )}
                  </button>
                  <p className="text-xs text-zinc-500 text-center mt-3">One purchase gives one {product.cards}-card game.</p>
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>
      <SiteFooter />
    </div>
  );
};

export default Premium;
